<template>
  <div id="apt-trade-wrapper">
    <div class="page-header">
      <div class="title">실거래가 조회</div>
      <div class="subtitle">국토교통부 신고 기준 서울시 아파트 매매 실거래가를 조건별로 검색합니다</div>
    </div>

    <form class="trade-filter" @submit.prevent="onClickSearch">
      <!-- 주요 필터 -->
      <div class="filter-grid">
        <div class="filter-item">
          <label for="sggCd">자치구</label>
          <select id="sggCd" v-model="sggCd">
            <option value="">전체</option>
            <option v-for="(name, code) in guOptions" :key="code" :value="code">
              {{ name }}
            </option>
          </select>
        </div>

        <div class="filter-item">
          <label for="umdNm">법정동</label>
          <input type="text" id="umdNm" v-model="umdNm" placeholder="예: 삼성동" />
        </div>

        <div class="filter-item">
          <label for="aptNm">아파트명</label>
          <input type="text" id="aptNm" v-model="aptNm" placeholder="예: 래미안퍼스티지" />
        </div>

        <div class="filter-item filter-date">
          <label>계약일</label>
          <div class="date-range">
            <input type="date" v-model="dealDateFrom" />
            <span class="tilde">~</span>
            <input type="date" v-model="dealDateTo" />
          </div>
        </div>
      </div>

      <!-- 상세 필터 (접기/펼치기) -->
      <transition name="adv">
        <div class="advanced-filters" v-show="showAdvanced">
          <div class="filter-grid">
            <div class="filter-item">
              <label>거래금액 (만원)</label>
              <div class="range-pair">
                <input type="number" v-model="minDealAmount" placeholder="최소 80000" />
                <span class="tilde">~</span>
                <input type="number" v-model="maxDealAmount" placeholder="최대 120000" />
              </div>
            </div>
            <div class="filter-item">
              <label>전용면적 (㎡)</label>
              <div class="range-pair">
                <input type="number" v-model="minExcluUseAr" placeholder="최소 45" />
                <span class="tilde">~</span>
                <input type="number" v-model="maxExcluUseAr" placeholder="최대 128" />
              </div>
            </div>
            <div class="filter-item">
              <label for="aptDong">아파트 동</label>
              <input type="text" id="aptDong" v-model="aptDong" placeholder="예: 101동" />
            </div>
            <div class="filter-item">
              <label for="floor">층수</label>
              <input type="number" id="floor" v-model="floor" placeholder="예: 15" />
            </div>
            <div class="filter-item">
              <label for="cdealType">거래 상태</label>
              <select id="cdealType" v-model="cdealType">
                <option value="">전체</option>
                <option value="O">해제된 거래</option>
              </select>
            </div>
          </div>
        </div>
      </transition>

      <!-- 액션 -->
      <div class="filter-actions">
        <button type="button" class="adv-toggle" @click="showAdvanced = !showAdvanced">
          상세 필터 <span class="chev" :class="{ open: showAdvanced }">▾</span>
        </button>
        <div class="action-btns">
          <button type="button" class="btn-reset" @click="onClickReset">초기화</button>
          <button type="submit" class="btn-search">조회</button>
        </div>
      </div>
    </form>

    <div class="trade-result">
      <div class="result-head">
        <div class="result-count">
          <template v-if="pagination.totalElements > 0">
            총 <strong>{{ pagination.totalElements.toLocaleString() }}</strong>건
          </template>
          <template v-else>검색 결과</template>
        </div>
        <div class="result-page" v-if="pagination.totalElements > 0">
          {{ pagination.pageNumber + 1 }} / {{ pagination.totalPages }} 페이지
        </div>
      </div>

      <div class="table-scroll">
        <div id="contents-area">
          <!-- 테이블 헤더 -->
          <div class="row header desktop-only">
            <div class="cell">계약일</div>
            <div class="cell">위치</div>
            <div class="cell">아파트</div>
            <div class="cell">층</div>
            <div class="cell">전용면적</div>
            <div class="cell align-right">거래금액</div>
            <div class="cell">상태</div>
            <div class="cell">관심</div>
          </div>
          <!-- 각 데이터 row -->
          <div
            v-for="(item, index) in contents"
            :key="index"
            class="row data-row"
            @click="openDetailModal(item)"
          >
            <div class="cell cell-date">
              <span class="label">계약일</span>
              {{ item.dealYear }}.{{ pad(item.dealMonth) }}.{{ pad(item.dealDay) }}
            </div>
            <div class="cell cell-loc">
              <span class="label">위치</span>
              <span class="loc-gu">{{ getGuName(item.sggCd) }}</span>
              <span class="loc-dong">{{ item.umdNm }}</span>
            </div>
            <div class="cell cell-apt">
              <span class="label">아파트</span>
              <span class="apt-name">{{ item.aptNm }}</span>
              <span class="apt-dong" v-if="item.aptDong">{{ item.aptDong }}</span>
            </div>
            <div class="cell">
              <span class="label">층</span>{{ item.floor }}층
            </div>
            <div class="cell cell-area">
              <span class="label">전용면적</span>
              {{ item.excluUseAr }}㎡
              <span class="pyeong">{{ toPyeong(item.excluUseAr) }}평</span>
            </div>
            <div class="cell cell-amount align-right">
              <span class="label">거래금액</span>
              <span class="amount">{{ toEok(item.dealAmount) }}억</span>
            </div>
            <div class="cell cell-status">
              <span class="label">상태</span>
              <span v-if="item.cdealType === 'O'" class="status-tag cancel">해제</span>
              <span v-else class="status-tag normal">정상</span>
            </div>
            <div class="cell favorite-cell" @click.stop="onClickFavorite(item)">
              <span class="label">관심</span>
              <button class="favorite-btn" title="관심 아파트 추가">&#9734;</button>
            </div>
          </div>

          <div class="empty-state" v-if="contents.length === 0">
            조건에 맞는 실거래 내역이 없습니다.
          </div>
        </div>
      </div>
    </div>
    <div id="empty"></div>
    <StickyNavigation
      parentSelector="#apt-trade-wrapper"
      class="sticky-navigation"
    >
      <PageNavigation
        v-if="pagination.totalElements > 0"
        :page-number="pagination.pageNumber"
        :total-pages="pagination.totalPages"
        @update:page="handlePageChange"
      />
    </StickyNavigation>

    <!-- 상세보기 모달 -->
    <div class="trade-modal-overlay" v-if="showModal" @click="closeModal">
      <div class="trade-modal" @click.stop>
        <div class="trade-modal-header">
          <h3>{{ modalAptNm }} - 거래 이력</h3>
          <button class="modal-close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="trade-modal-body">
          <div v-if="modalLoading" class="modal-loading">불러오는 중...</div>
          <div v-else-if="modalTrades.length === 0" class="modal-empty">거래 이력이 없습니다.</div>
          <div v-else class="modal-trade-list">
            <div class="modal-trade-header">
              <span>계약일</span>
              <span>동</span>
              <span>층</span>
              <span>평수</span>
              <span>거래금액</span>
              <span>해지</span>
            </div>
            <div
              v-for="(t, i) in modalTrades"
              :key="i"
              class="modal-trade-row"
            >
              <span>{{ t.dealYear }}.{{ t.dealMonth }}.{{ t.dealDay }}</span>
              <span>{{ t.aptDong || '-' }}</span>
              <span>{{ t.floor }}층</span>
              <span>{{ t.excluUseAr }}평</span>
              <span>{{ toEok(t.dealAmount) }}억</span>
              <span :class="{ 'cancel-tag': t.cdealType === 'O' }">{{ t.cdealType === 'O' ? '해지' : '' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { AptTrade } from "../class";
import { PageResponse } from "@/common/class/index";
import StickyNavigation from "@/common/components/StickyNavigation.vue";
import PageNavigation from "@/common/components/PageNavigation.vue";

export default defineComponent({
  name: "AptTradeView",
  components: { StickyNavigation, PageNavigation },
  setup() {
    return {};
  },

  data() {
    return {
      pagination: new PageResponse(),

      guOptions: {
        "11110": "종로구",
        "11140": "중구",
        "11170": "용산구",
        "11200": "성동구",
        "11215": "광진구",
        "11230": "동대문구",
        "11260": "중랑구",
        "11290": "성북구",
        "11305": "강북구",
        "11320": "도봉구",
        "11350": "노원구",
        "11380": "은평구",
        "11410": "서대문구",
        "11440": "마포구",
        "11470": "양천구",
        "11500": "강서구",
        "11530": "구로구",
        "11545": "금천구",
        "11560": "영등포구",
        "11590": "동작구",
        "11620": "관악구",
        "11650": "서초구",
        "11680": "강남구",
        "11710": "송파구",
        "11740": "강동구",
      } as { [key: string]: string },
      sggCd: "", // 법정동시군구코드
      umdNm: "", // 법정동
      aptNm: "", // 아파트명
      dealDateFrom: "", // 계약일 시작일자
      dealDateTo: "", // 계약일 종료일자
      minDealAmount: "", // 최소 거래금액
      maxDealAmount: "", // 최대 거래금액
      minExcluUseAr: null,
      maxExcluUseAr: null,
      cdealType: "", // 해제여부
      aptDong: "", // 아파트 동명
      floor: "", // 층수
      showAdvanced: false, // 상세 필터 펼침
      //
      page: 0,
      size: 10,
      //
      contents: [] as AptTrade[],
      // 모달
      showModal: false,
      modalAptNm: "",
      modalTrades: [] as any[],
      modalLoading: false,
    };
  },

  mixins: [UtilMixin],
  computed: {
    //
  },
  methods: {
    //
    initDefaultData() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();

      this.dealDateFrom = new Date(currentYear, currentMonth - 1, 2)
        .toISOString()
        .split("T")[0];
      this.dealDateTo = new Date(currentYear, currentMonth + 1, 1)
        .toISOString()
        .split("T")[0];
    },

    toEok(amount: number): string {
      return (Math.ceil(amount / 100) / 100).toFixed(2);
    },
    toPyeong(m2: number | string): string {
      const v = Number(m2);
      if (!v || isNaN(v)) return "-";
      return (v / 3.3058).toFixed(1);
    },
    pad(n: number | string): string {
      return String(n).padStart(2, "0");
    },
    getGuName(code: string) {
      return this.guOptions[code] || "알 수 없음";
    },
    async onClickSearch() {
      this.pagination.pageNumber = 0;
      this.pagination.totalElements = 0;
      this.fetchData();
    },
    onClickReset() {
      this.sggCd = "";
      this.umdNm = "";
      this.aptNm = "";
      this.minDealAmount = "";
      this.maxDealAmount = "";
      this.minExcluUseAr = null;
      this.maxExcluUseAr = null;
      this.cdealType = "";
      this.aptDong = "";
      this.floor = "";
      this.initDefaultData();
    },
    async handlePageChange(page: number) {
      this.pagination.pageNumber = page - 1;
      this.fetchData();
    },
    async fetchData() {

      const payload = Object.fromEntries(
        Object.entries({
          sggCd: this.sggCd,
          umdNm: this.umdNm,
          aptNm: this.aptNm,
          dealDateFrom: this.dealDateFrom,
          dealDateTo: this.dealDateTo,
          minDealAmount: this.minDealAmount,
          maxDealAmount: this.maxDealAmount,
          cdealType: this.cdealType,
          minExcluUseAr: this.minExcluUseAr,
          maxExcluUseAr: this.maxExcluUseAr,
          aptDong: this.aptDong,
          floor: this.floor,
          page: this.pagination.pageNumber,
          size: this.size,
        }).filter(
          ([_, value]) => value !== null && value !== undefined && value !== ""
        )
      );

      const res = await this.getApi().apt().getAptTrade(payload);
      this.contents = res.content;
      this.pagination = new PageResponse().bind(res);
    },
    async openDetailModal(item: any) {
      this.showModal = true;
      this.modalAptNm = `${this.getGuName(item.sggCd)} ${item.umdNm} ${item.aptNm}`;
      this.modalLoading = true;
      this.modalTrades = [];

      try {
        const res = await this.getApi().apt().getAptTrade({
          sggCd: item.sggCd,
          aptNm: item.aptNm,
          size: 50,
          page: 0,
        });
        this.modalTrades = res.content || [];
      } catch {
        this.modalTrades = [];
      } finally {
        this.modalLoading = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.modalTrades = [];
    },
    async onClickFavorite(item: any) {
      if (!confirm(`${item.aptNm}을(를) 관심 아파트에 추가하시겠습니까?`)) return;
      try {
        await this.getApi().apt().addAptFavorite({
          sggCd: item.sggCd,
          umdCd: item.umdCd,
          aptNm: item.aptNm,
        });
        alert("관심 아파트에 추가되었습니다.");
      } catch {
        alert("추가에 실패했습니다.");
      }
    },
  },

  created() {
    this.initDefaultData();
    this.fetchData();
  },
});
</script>
